/**
 * DealNextActionCard - Card "Próxima ação" da cadência ativa
 *
 * Item 2.9 do checklist Playbook de Vendas.
 *
 * Aparece no topo da aba Resumo do DealDetailsModalPremium quando o negócio
 * tem uma cadência ativa. Mostra a próxima atividade pendente, prazo, badge
 * de overdue e ações rápidas (concluir/pular).
 *
 * @version 1.2.0 - Barra de progresso e % movidos para a mesma linha do
 *                  título (compacto inline, w-16 h-1.5), igual ao header do
 *                  checklist de tarefas. Metadata de prazo/passo mantida.
 * @version 1.1.0 - Redesign "Enterprise denso": layout SaaS profissional com
 *                  barra de status lateral, hierarquia tipográfica forte e
 *                  micro-barra de progresso. Lógica preservada 1:1.
 */

import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CheckCircle2, SkipForward, Zap, Clock, AlertTriangle, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDealCadence } from '@/hooks/useDealCadence';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface DealNextActionCardProps {
  dealId: string;
}

/**
 * Card destacado da próxima atividade pendente da cadência ativa.
 */
export function DealNextActionCard({ dealId }: DealNextActionCardProps) {
  const { nextAction, completeStep, skipStep } = useDealCadence(dealId);
  const { userRole } = usePermissions();
  const { toast } = useToast();

  // Sem cadência ativa → não renderiza
  if (!nextAction) return null;

  // Apenas admins podem pular nesta versão (permissão granular vem em 2.12)
  const canSkip = userRole === 'root_admin' || userRole === 'tenant_admin';

  // Formatação de prazo
  const dueLabel = nextAction.due_at
    ? formatDistanceToNow(new Date(nextAction.due_at), { addSuffix: true, locale: ptBR })
    : 'sem prazo';
  const dueExact = nextAction.due_at
    ? format(new Date(nextAction.due_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
    : null;

  const handleComplete = async () => {
    try {
      await completeStep.mutateAsync(nextAction.step_id);
      toast({ title: 'Atividade concluída', description: 'Próximo passo foi destravado.' });
    } catch (e: any) {
      toast({ title: 'Erro ao concluir', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  const handleSkip = async () => {
    try {
      await skipStep.mutateAsync({ stepId: nextAction.step_id });
      toast({ title: 'Atividade pulada' });
    } catch (e: any) {
      toast({ title: 'Erro ao pular', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  const progressPct = nextAction.total_steps > 0
    ? Math.round((nextAction.completed_steps / nextAction.total_steps) * 100)
    : 0;

  const overdue = nextAction.is_overdue;

  return (
    // Container do card: branco, borda fina, sombra suave, barra de status absoluta na esquerda
    <div className="relative w-full bg-card rounded-lg border border-border shadow-sm overflow-hidden">
      {/* Barra vertical de status — semântica: destructive quando atrasada, primary em dia */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-1',
          overdue ? 'bg-destructive' : 'bg-primary',
        )}
        aria-hidden
      />

      <div className="p-3 pl-4">
        <div className="flex flex-col gap-2.5">
          {/* Linha de cabeçalho: identidade + badges à esquerda, ações à direita (desktop) */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {/* Chip-ícone tonal */}
              <div
                className={cn(
                  'w-6 h-6 rounded-md flex items-center justify-center shrink-0',
                  overdue ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary',
                )}
              >
                <Zap className="w-3 h-3" />
              </div>

              {/* Label da seção */}
              <span className="text-[10px] font-bold tracking-widest text-muted-foreground uppercase">
                Próxima Ação
              </span>

              {/* Badges: playbook + status atrasada */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge
                  variant="outline"
                  className="text-[9px] font-semibold uppercase tracking-tight h-4 px-1.5 bg-muted/50"
                >
                  {nextAction.playbook_name}
                </Badge>
                {overdue && (
                  <Badge
                    variant="destructive"
                    className="text-[9px] font-semibold uppercase tracking-tight h-4 px-1.5 gap-0.5"
                  >
                    <AlertTriangle className="w-2.5 h-2.5" />
                    Atrasada
                  </Badge>
                )}
              </div>
            </div>

            {/* Botões de ação — desktop */}
            <div className="hidden sm:flex items-center gap-1.5 shrink-0">
              <Button
                size="xs"
                onClick={handleComplete}
                disabled={completeStep.isPending}
                className="font-semibold h-7 text-xs px-2.5"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Concluir
              </Button>
              {canSkip && (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={handleSkip}
                  disabled={skipStep.isPending}
                  className="font-semibold h-7 text-xs px-2.5"
                >
                  <SkipForward className="w-3 h-3 mr-1" />
                  Pular
                </Button>
              )}
            </div>
          </div>

          {/* Título do passo + barra de progresso compacta inline (v1.2.0) */}
          <div className="flex items-center gap-2 min-w-0">
            <h3 className="text-sm font-semibold text-foreground leading-tight truncate flex-1">
              {nextAction.title}
            </h3>
            <span className="text-xs font-semibold text-foreground tabular-nums shrink-0">
              {progressPct}%
            </span>
            {/* Micro-barra de progresso compacta — mesma linha do título */}
            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden shrink-0">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  overdue ? 'bg-destructive' : 'bg-primary',
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>

          {/* Metadata — prazo e passo (mantida no lugar atual, sem %) */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1" title={dueExact ?? undefined}>
              <Clock className="w-3 h-3" />
              {dueLabel}
            </span>
            <span className="flex items-center gap-1">
              <ListChecks className="w-3 h-3" />
              Passo {nextAction.sort_order}/{nextAction.total_steps}
              <span className="text-muted-foreground/70">
                · {nextAction.completed_steps} concluídos
              </span>
            </span>
          </div>

          {/* Botões de ação — mobile (full-width) */}
          <div className="flex sm:hidden gap-1.5">
            <Button
              size="xs"
              onClick={handleComplete}
              disabled={completeStep.isPending}
              className="flex-1 font-semibold h-7 text-xs"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Concluir
            </Button>
            {canSkip && (
              <Button
                size="xs"
                variant="outline"
                onClick={handleSkip}
                disabled={skipStep.isPending}
                className="flex-1 font-semibold h-7 text-xs"
              >
                <SkipForward className="w-3 h-3 mr-1" />
                Pular
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
